import { useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { TextField, FilterOptionsState, Box } from '@mui/material'
import { Button, MultipleAutocomplete } from '@carto/meridian-ds/components'
import { Group } from 'features/groups'
import intl from 'app/localization/intl'
import GroupsTags from './GroupsTags'
import GroupAvatar from 'features/account/ui/AccountGroups/AccountGroupsTable/GroupAvatar'
import { containsIgnoreCase } from 'features/common/utils/containsIgnoreCase'
import SharedWithItemsTable from '../UsersSelector/SharedWithItemsTable/SharedWithItemsTable'
import { SelectorItem } from '../UsersSelector/UserOrGroupSelector'
import { useSharingContext } from '../../../SharingContext'

function GroupsSelector({
  groups,
  selectedGroups,
  onSelectedGroupsChange,
  isLoading,
  disabled,
  readonly
}: {
  groups: Group[]
  selectedGroups: Group[]
  onSelectedGroupsChange: (arg: Group[]) => void
  isLoading?: boolean
  disabled?: boolean
  readonly?: boolean
}) {
  const { setDirty } = useSharingContext()
  const [draftSelectedGroups, setDraftSelectedGroups] = useState<Group[]>([])

  useEffect(() => {
    setDirty?.(!!draftSelectedGroups.length)
  }, [draftSelectedGroups, setDirty])

  const selectedItems = useMemo<SelectorItem[]>(() => {
    return selectedGroups.map((group) => ({ id: group.id, label: group.customName, data: group, type: 'group' }))
  }, [selectedGroups])

  const formattedOptions = useMemo(() => {
    return groups.map((group) => {
      return {
        ...group,
        startAdornment: <GroupAvatar group={group} aria-label={group.externalName} />,
        extended: true
      }
    })
  }, [groups])

  const handleUnselectGroup = (group: Group) => {
    const selectedGroupsCopy = [...draftSelectedGroups]
    selectedGroupsCopy.splice(
      selectedGroupsCopy.findIndex((g) => g.id === group.id),
      1
    )
    setDraftSelectedGroups(selectedGroupsCopy)
  }

  const handleSelectGroup = (newSelectedGroups: Group[]) => {
    setDraftSelectedGroups(newSelectedGroups)
  }

  const handleShare = () => {
    onSelectedGroupsChange([...selectedGroups, ...draftSelectedGroups])
    setDraftSelectedGroups([])
  }

  const handleRevoke = (item: SelectorItem) => {
    const selectedItemsCopy = [...selectedItems]
    selectedItemsCopy.splice(
      selectedItemsCopy.findIndex((g) => g.id === item.id),
      1
    )
    onSelectedGroupsChange(selectedItemsCopy.map((i) => i.data) as Group[])
  }

  const filterOptions = (options: Group[], state: FilterOptionsState<Group>): Group[] => {
    const matchesInputValue = ({ customName, externalName }: Group): boolean =>
      [customName, externalName].some((value) => containsIgnoreCase(value, state.inputValue))
    return options.filter(matchesInputValue)
  }

  return (
    <Box>
      <Box display="flex">
        <MultipleAutocomplete
          options={formattedOptions}
          disabled={disabled}
          value={draftSelectedGroups}
          getOptionDisabled={(option) => selectedGroups.some((sg) => sg.id === option.id)}
          filterOptions={filterOptions}
          noOptionsText={intl.formatMessage({ id: 'common.shareDialog.shareWithGroups.notFound' })}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.customName)}
          limitTags={2}
          loading={isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={intl.formatMessage({ id: 'common.shareDialog.shareWithGroups.placeholder' })}
              variant="filled"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => handleSelectGroup(value as Group[])}
          renderTags={(groups, _, state) => {
            if (isLoading) return null
            return <GroupsTags groups={groups} onDelete={handleUnselectGroup} {...state} />
          }}
          extended
        />
        <Button
          variant="contained"
          sx={{ ml: (theme) => theme.spacing(1), flex: '0 0 auto' }}
          onClick={handleShare}
          disabled={!draftSelectedGroups.length}
        >
          <FormattedMessage id="common.actions.share" />
        </Button>
      </Box>
      <Box mt={1}>
        <SharedWithItemsTable items={selectedItems} onRevoke={handleRevoke} onlyGroups readonly={readonly} />
      </Box>
    </Box>
  )
}

export default GroupsSelector
